import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    Grid,
    CircularProgress,
    Alert,
} from '@mui/material'

import { MedicalActTemplate } from '@/core/models/medical-act.model'
import { useMedicalActTemplateForm } from '../model/useMedicalActTemplateForm'

type MedicalActTemplateFormProps = {
    open: boolean
    onClose: () => void
    template: MedicalActTemplate | null
}

export function MedicalActTemplateForm({ open, onClose, template }: MedicalActTemplateFormProps) {
    const { t } = useTranslation()

    const { rhForm, handleSubmit, isEditing, isLoading, error } = useMedicalActTemplateForm({
        template,
        onSuccess: onClose,
    })

    const { control, formState: { errors } } = rhForm

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <form onSubmit={rhForm.handleSubmit(handleSubmit)}>
                <DialogTitle>
                    {isEditing
                        ? t('page.admin.medicalActTemplates.form.editTitle', { defaultValue: 'Edit Template' })
                        : t('page.admin.medicalActTemplates.form.createTitle', { defaultValue: 'Create Template' })
                    }
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {t('common.error.save', { defaultValue: 'Error saving data' })}
                        </Alert>
                    )}

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Controller
                                name="name"
                                control={control}
                                rules={{ required: t('common.validation.required', { defaultValue: 'This field is required' }) }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={t('common.name', { defaultValue: 'Name' })}
                                        fullWidth
                                        required
                                        error={!!errors.name}
                                        helperText={errors.name?.message}
                                        placeholder={t('page.admin.medicalActTemplates.form.namePlaceholder', { defaultValue: 'e.g., Remote Consultation' })}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="code"
                                control={control}
                                rules={{ required: t('common.validation.required', { defaultValue: 'This field is required' }) }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={t('common.code', { defaultValue: 'Code' })}
                                        fullWidth
                                        required
                                        error={!!errors.code}
                                        helperText={errors.code?.message}
                                        placeholder={t('page.admin.medicalActTemplates.form.codePlaceholder', { defaultValue: 'e.g., CONS_REMOTE' })}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="price"
                                control={control}
                                rules={{
                                    required: t('common.validation.required', { defaultValue: 'This field is required' }),
                                    min: { value: 0, message: t('common.validation.minValue', { defaultValue: 'Value must be positive', value: 0 }) },
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        label={t('common.price', { defaultValue: 'Price' })}
                                        fullWidth
                                        required
                                        error={!!errors.price}
                                        helperText={errors.price?.message}
                                        inputProps={{ min: 0 }}
                                        onChange={(e) => field.onChange(Number(e.target.value))}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Controller
                                name="description"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={t('common.description', { defaultValue: 'Description' })}
                                        fullWidth
                                        multiline
                                        rows={2}
                                        placeholder={t('page.admin.medicalActTemplates.form.descriptionPlaceholder', { defaultValue: 'Optional description' })}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="icd10Code"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={t('page.admin.medicalActTemplates.form.icd10Code', { defaultValue: 'ICD-10 Code' })}
                                        fullWidth
                                        placeholder="e.g., Z00.00"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="icd10Description"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={t('page.admin.medicalActTemplates.form.icd10Description', { defaultValue: 'ICD-10 Description' })}
                                        fullWidth
                                        placeholder="e.g., General examination"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="isGlobal"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={<Switch checked={field.value} onChange={field.onChange} />}
                                        label={t('page.admin.medicalActTemplates.form.isGlobal', { defaultValue: 'Global Template' })}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="isActive"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={<Switch checked={field.value} onChange={field.onChange} />}
                                        label={t('page.admin.medicalActTemplates.form.isActive', { defaultValue: 'Active' })}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isLoading}>
                        {t('common.cancel', { defaultValue: 'Cancel' })}
                    </Button>
                    <Button type="submit" variant="contained" disabled={isLoading}>
                        {isLoading ? <CircularProgress size={20} /> : t('common.save', { defaultValue: 'Save' })}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}

export default MedicalActTemplateForm
